import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface User {
  id: string
  email: string
  role: 'admin' | 'reviewer' | 'user'
  name?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('xarlie_admin_token')
    const storedUser = localStorage.getItem('xarlie_admin_user')

    if (storedToken && storedUser) {
      try {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
      } catch (e) {
        console.error('Failed to parse stored user:', e)
        localStorage.removeItem('xarlie_admin_token')
        localStorage.removeItem('xarlie_admin_user')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    console.log('[Auth] Attempting login for:', email)

    const response = await fetch(`${API_BASE_URL}/v1/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }))
      console.error('[Auth] Login failed:', error)
      throw new Error(error.message || 'Invalid credentials')
    }

    const data = await response.json()
    console.log('[Auth] Login successful, storing credentials')

    setToken(data.token)
    setUser(data.user)

    localStorage.setItem('xarlie_admin_token', data.token)
    localStorage.setItem('xarlie_admin_user', JSON.stringify(data.user))
  }

  const logout = () => {
    console.log('[Auth] Logging out')
    setToken(null)
    setUser(null)
    localStorage.removeItem('xarlie_admin_token')
    localStorage.removeItem('xarlie_admin_user')
  }

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'admin' || user?.role === 'reviewer',
    login,
    logout,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
